import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toolsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import {
  Popover, PopoverContent, PopoverTrigger
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Search, Eye, ArrowUp, ArrowDown, ArrowUpDown, Filter, X } from 'lucide-react';
import AddToolDialog from '@/components/AddToolDialog';

const statusMap = {
  active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  recalled_for_calibration: { label: 'Recalled', className: 'bg-red-50 text-red-700 border-red-200' },
  scrap: { label: 'Scrap', className: 'bg-slate-100 text-slate-600 border-slate-200' },
  under_repair: { label: 'Under Repair', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  not_in_use: { label: 'Not In Use', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  overdue: { label: 'Overdue', className: 'bg-red-100 text-red-800 border-red-300' },
  due_soon: { label: 'Due Soon', className: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const columns = [
  { key: 'instrument_name', label: 'Instrument Name', sortable: true },
  { key: 'instrument_code', label: 'Code', sortable: true },
  { key: 'serial_number', label: 'Serial No.', sortable: true },
  { key: 'certification_no', label: 'Cert. No.', sortable: true },
  { key: 'method', label: 'Method', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'calibration_date', label: 'Cal. Date', sortable: true },
  { key: 'due_on', label: 'Due On', sortable: true },
  { key: '_actions', label: '', sortable: false },
];

export default function ToolsPage() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const { canEdit, canMakeEntries } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Allow both manager, quality_manager, admin, and app_user to create/edit tools
  const canCreateTools = canEdit || canMakeEntries('calibration_cms');

  // Multi-select status filter from URL
  const statusFilter = searchParams.get('status') || '';
  const selectedStatuses = statusFilter ? statusFilter.split(',') : [];
  
  const setSelectedStatuses = (statuses) => {
    if (statuses.length === 0) {
      searchParams.delete('status');
    } else {
      searchParams.set('status', statuses.join(','));
    }
    setSearchParams(searchParams, { replace: true });
  };
  
  const toggleStatus = (val) => {
    if (selectedStatuses.includes(val)) {
      setSelectedStatuses(selectedStatuses.filter(s => s !== val));
    } else {
      setSelectedStatuses([...selectedStatuses, val]);
    }
  };

  const loadTools = useCallback(async () => {
    try {
      const params = {};
      if (selectedStatuses.length > 0) params.status = selectedStatuses.join(',');
      if (search) params.search = search;
      const res = await toolsAPI.list(params);
      setTools(res.data);
    } catch (err) {
      toast.error('Failed to load instruments');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    setLoading(true);
    const timeout = setTimeout(loadTools, 300);
    return () => clearTimeout(timeout);
  }, [loadTools]);

  // Sorting logic
  const sortedTools = useMemo(() => {
    if (!sortKey) return tools;
    return [...tools].sort((a, b) => {
      let aVal = a[sortKey] || '';
      let bVal = b[sortKey] || '';
      // For status, sort by label
      if (sortKey === 'status') {
        aVal = statusMap[aVal]?.label || aVal;
        bVal = statusMap[bVal]?.label || bVal;
      }
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tools, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortKey(''); setSortDir('asc'); } // third click clears sort
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ colKey }) => {
    if (sortKey !== colKey) return <ArrowUpDown className="w-3 h-3 text-slate-300 ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-[#0F172A] ml-1" />
      : <ArrowDown className="w-3 h-3 text-[#0F172A] ml-1" />;
  };

  const getDueBadge = (nextDate, status) => {
    if (!nextDate) return null;
    if (status === 'not_in_use' || status === 'scrap') return null;
    const now = new Date();
    const due = new Date(nextDate);
    const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return (
      <Badge
        variant="outline"
        className="text-[10px] bg-red-100 text-red-800 border-red-300 cursor-pointer hover:opacity-80"
        onClick={(e) => { e.stopPropagation(); setSelectedStatuses(['overdue']); }}
        data-testid="overdue-badge"
      >
        Overdue
      </Badge>
    );
    if (diff <= 15) return (
      <Badge
        variant="outline"
        className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 cursor-pointer hover:opacity-80"
        onClick={(e) => { e.stopPropagation(); setSelectedStatuses(['due_soon']); }}
        data-testid="due-soon-badge"
      >
        Due in {diff}d
      </Badge>
    );
    return null;
  };

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'due_soon', label: 'Due Soon (15d)' },
    { value: 'recalled_for_calibration', label: 'Recalled' },
    { value: 'under_repair', label: 'Under Repair' },
    { value: 'not_in_use', label: 'Not In Use' },
    { value: 'scrap', label: 'Scrap' },
  ];

  const filterLabel = selectedStatuses.length === 0 
    ? 'All Status' 
    : selectedStatuses.length === 1 
      ? (statusMap[selectedStatuses[0]]?.label || selectedStatuses[0])
      : `${selectedStatuses.length} selected`;

  return (
    <div data-testid="tools-page" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">Instruments Inventory</h1>
          <p className="text-sm text-slate-500 mt-0.5">{sortedTools.length} instrument{sortedTools.length !== 1 ? 's' : ''} {selectedStatuses.length > 0 ? '(filtered)' : 'registered'}</p>
        </div>
        {canCreateTools && (
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white gap-2"
            data-testid="add-tool-button"
          >
            <Plus className="w-4 h-4" />
            Add Instrument
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, serial, code, location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="tools-search-input"
          />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className={`h-9 gap-1.5 text-xs ${selectedStatuses.length > 0 ? 'border-emerald-500 text-emerald-700' : ''}`} data-testid="status-filter-trigger">
              <Filter className="w-3.5 h-3.5" />
              {filterLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-2" align="start">
            <div className="space-y-1">
              {statusOptions.map(opt => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                  data-testid={`status-option-${opt.value}`}
                >
                  <Checkbox
                    checked={selectedStatuses.includes(opt.value)}
                    onCheckedChange={() => toggleStatus(opt.value)}
                    className="h-3.5 w-3.5"
                  />
                  <span className="text-xs">{opt.label}</span>
                </label>
              ))}
            </div>
            {selectedStatuses.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedStatuses([])}
                className="w-full mt-2 text-xs text-slate-500"
                data-testid="clear-all-filters"
              >
                <X className="w-3 h-3 mr-1" /> Clear all
              </Button>
            )}
          </PopoverContent>
        </Popover>
        {/* Active filter tags */}
        {selectedStatuses.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {selectedStatuses.map(s => (
              <Badge
                key={s}
                variant="outline"
                className="text-[10px] gap-1 cursor-pointer hover:bg-slate-100 pr-1.5"
                onClick={() => toggleStatus(s)}
                data-testid={`filter-tag-${s}`}
              >
                {statusMap[s]?.label || s}
                <span className="text-slate-400 ml-0.5">x</span>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              {columns.map(col => (
                <TableHead
                  key={col.key}
                  className={`text-xs font-semibold text-slate-500 ${col.sortable ? 'cursor-pointer select-none hover:text-[#0F172A] transition-colors' : ''} ${col.key === '_actions' ? 'w-[50px]' : ''}`}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  data-testid={col.sortable ? `sort-${col.key}` : undefined}
                >
                  <div className="flex items-center">
                    {col.label}
                    {col.sortable && <SortIcon colKey={col.key} />}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-sm text-slate-400">Loading...</TableCell>
              </TableRow>
            ) : sortedTools.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-sm text-slate-400">
                  No instruments found
                </TableCell>
              </TableRow>
            ) : sortedTools.map(tool => {
              const status = statusMap[tool.status] || statusMap.active;
              return (
                <TableRow 
                  key={tool.id} 
                  className="cursor-pointer hover:bg-slate-50/50"
                  onClick={() => navigate(`/quality/calibration/${tool.id}`)}
                  data-testid={`tool-row-${tool.id}`}
                >
                  <TableCell className="font-medium text-sm text-[#0F172A]">{tool.instrument_name}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{tool.instrument_code}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{tool.serial_number}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{tool.certification_no || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-600">{tool.method || '-'}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`text-[10px] cursor-pointer hover:opacity-80 ${status.className}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedStatuses([tool.status]);
                      }}
                      data-testid={`filter-status-${tool.status}`}
                    >
                      {status.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs font-mono text-slate-500">{tool.calibration_date?.substring(0, 10) || '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-mono text-slate-500">
                        {tool.due_on?.substring(0, 10) || tool.next_calibration_date?.substring(0, 10) || '-'}
                      </span>
                      {getDueBadge(tool.due_on || tool.next_calibration_date, tool.status)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`view-tool-${tool.id}`}>
                      <Eye className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <AddToolDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={loadTools} />
    </div>
  );
}
